import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import gsap from 'gsap';

const Model = forwardRef(({ mousePosition, audioLevel, jawRotations, audioContextRef, analyserRef, onAudioStart, onAudioEnd }, ref) => {
  const gltf = useGLTF('/aa4.gltf');
  const modelRef = useRef();
  const { viewport } = useThree();
  const audioRef = useRef(null);

  // Expose playAudio method through ref
  useImperativeHandle(ref, () => ({
    playAudio: async (audioUrl) => {
      console.log('Model: playAudio called with URL:', audioUrl);
      try {
        if (audioRef.current) {
          console.log('Model: Stopping previous audio');
          audioRef.current.pause();
          audioRef.current = null;
        }

        console.log('Model: Creating new Audio element');
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        // Wait for audio context to be ready
        if (audioContextRef.current?.state === 'suspended') {
          console.log('Model: Resuming audio context');
          await audioContextRef.current.resume();
        }

        // Create and connect audio nodes
        if (audioContextRef.current && analyserRef.current) {
          console.log('Model: Setting up audio nodes');
          const source = audioContextRef.current.createMediaElementSource(audio);
          
          // Connect nodes directly for clear audio
          console.log('Model: Connecting audio nodes');
          source
            .connect(analyserRef.current)
            .connect(audioContextRef.current.destination);

          // Set up analyzer
          analyserRef.current.fftSize = 2048;
          analyserRef.current.smoothingTimeConstant = 0.8;
          const bufferLength = analyserRef.current.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);

          // Function to analyze audio
          const analyzeAudio = () => {
            if (!audioRef.current || audioRef.current.paused) return;

            analyserRef.current.getByteTimeDomainData(dataArray);
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
              const amplitude = (dataArray[i] - 128) / 128;
              sum += amplitude * amplitude;
            }
            const rms = Math.sqrt(sum / bufferLength);
            console.log('Model: Audio RMS:', rms);
          };

          // Start analysis loop
          const analysisInterval = setInterval(analyzeAudio, 50);

          // Set up event handlers
          audio.onplay = () => {
            console.log('Model: Audio started playing');
            onAudioStart?.();
          };

          audio.onended = () => {
            console.log('Model: Audio finished playing');
            onAudioEnd?.();
            audioRef.current = null;
            clearInterval(analysisInterval);
          };

          audio.onerror = (error) => {
            console.error('Model: Audio playback error:', error);
            clearInterval(analysisInterval);
          };

          // Start playback
          console.log('Model: Starting audio playback');
          await audio.play();
          console.log('Model: Audio playback started successfully');
        }
      } catch (error) {
        console.error('Model: Error playing audio:', error);
        onAudioEnd?.();
        throw error; // Re-throw to handle in parent
      }
    }
  }));

  // Helper function to create distortion curve
  const createDistortionCurve = (amount) => {
    const samples = 44100;
    const curve = new Float32Array(samples);
    const deg = Math.PI / 180;

    for (let i = 0; i < samples; ++i) {
      const x = (i * 2) / samples - 1;
      curve[i] = ((3 + amount) * x * 20 * deg) / (Math.PI + amount * Math.abs(x));
    }
    return curve;
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Make model shiny and add detailed bump mapping
  useEffect(() => {
    if (gltf.scene) {
      const textureLoader = new THREE.TextureLoader();
      const bumpTexture = textureLoader.load('/Image_0.jpg');
            
      gltf.scene.traverse((child) => {
        if (child.isMesh) {
          child.material.metalness = 0.3; // Slightly increased metalness
          child.material.roughness = 0.8; // Reduced roughness for more shine
          child.material.envMapIntensity = 1; // Enhanced environment reflections
          child.material.bumpMap = bumpTexture;
          child.material.bumpScale = 0.2; // Increased bump intensity
          child.material.needsUpdate = true;
        }
      });
    }
  }, [gltf]);

  // Set default positions
  useEffect(() => {
    if (modelRef.current) {
      modelRef.current.traverse((object) => {
        if (object.isBone) {
          switch(object.name) {
            // Spine & Neck chain
            case 'LowerSpine':
              object.rotation.x = -0.01438760757446289;
              object.rotation.y = 1.1925877174689958e-07;
              object.rotation.z = -1.7160264320636998e-09;
              break;
            case 'UpperSpine':
              object.rotation.x = -1.862645149230957e-09;
              object.rotation.y = 2.8545695585080466e-08;
              object.rotation.z = -5.550228914904718e-16;
              break;
            case 'Neck':
              object.rotation.x = 0.0023247082717716694;
              object.rotation.y = -7.018355319132752e-08;
              object.rotation.z = 5.241048151027883e-10;
              break;
            case 'Head':
              object.rotation.x = 0.009809021838009357;
              object.rotation.y = 4.697433908518178e-08;
              object.rotation.z = 1.98380467608672e-09;
              break;

            // Arms
            case 'LeftArm':
              object.rotation.x = 0;  // Slight forward tilt
              object.rotation.y = 0;
              object.rotation.z = -Math.PI / 1.1; // Slightly away from body
              break;
            case 'LeftForearm':
              object.rotation.x = 0;
              object.rotation.y = 0;
              object.rotation.z = 0;  // Slight bend at elbow
              break;
            case 'RightArm':
              object.rotation.x = 0;  // Slight forward tilt
              object.rotation.y = 0;
              object.rotation.z = -Math.PI / 0.9; // Slightly away from body
              break;
            case 'RightForearm':
              object.rotation.x = 0;
              object.rotation.y = 0;
              object.rotation.z = 0; // Slight bend at elbow
              break;
            // Tail
            case 'TailStart':
              object.rotation.x = -0.5713699460029602;
              object.rotation.y = -9.788494281792737e-08;
              object.rotation.z = -6.814794772935784e-08;
              break;
            case 'TailEnd':
              object.rotation.x = 0.24860985577106476;
              object.rotation.y = 9.178654818984002e-17;
              object.rotation.z = 1.4647925410051117e-14;
              break;

            // Legs
            case 'HipRight':
              object.rotation.x = -0.8816745281219482;
              object.rotation.y = 0.47185802459716797;
              object.rotation.z = 7.182914885106584e-08;
              break;
            case 'RightLeg':
              object.rotation.x = 3.597183351189415e-08;
              object.rotation.y = 9.287489888265554e-08;
              object.rotation.z = -0.4718579947948456;
              break;
            case 'RightFoot':
              object.rotation.x = -0.3207886815071106;
              object.rotation.y = -0.32078900933265686;
              object.rotation.z = 0.6301541924476624;
              break;
            case 'HipLeft':
              object.rotation.x = -0.8816745281219482;
              object.rotation.y = -0.47185805439949036;
              object.rotation.z = -1.0985636578197955e-07;
              break;
            case 'LeftLeg':
              object.rotation.x = -3.911006274393003e-08;
              object.rotation.y = -1.495759960334908e-07;
              object.rotation.z = 0.47185805439949036;
              break;
            case 'LeftFoot':
              object.rotation.x = -0.28076422214508057;
              object.rotation.y = 0.2807645797729492;
              object.rotation.z = -0.6489772796630859;
              break;
          }
        }
      });
    }
  }, []);

  // Handle lip sync animation
  useEffect(() => {
    if (modelRef.current && typeof audioLevel === 'number') {
      modelRef.current.traverse((object) => {
        if (object.isBone && object.name === 'LowerJaw') {
          const closedX = -1.0;
          const openX = -0.3;
          const jawRange = openX - closedX;
          const targetRotation = closedX + (audioLevel * jawRange);

          // Animate with GSAP
          gsap.to(object.rotation, {
            x: targetRotation,
            duration: 0.1,
            ease: "power2.out",
            overwrite: true
          });

          // Keep Y and Z rotations constant
          object.rotation.y = 4.192765024058076e-10;
          object.rotation.z = -1.9721701702279937e-11;
        }
      });
    }
  }, [audioLevel]);

  useFrame(({ clock }) => {
    if (modelRef.current) {
      modelRef.current.traverse((object) => {
        // Handle both Head and Head.001 rotations
        if (object.isBone && (object.name === "Head" || object.name === "Head001")) {
          const targetX = mousePosition.x * viewport.width / 2;
          const targetY = mousePosition.y * viewport.height / 2;
          const targetZ = 3;
          
          // Calculate the target angles with reduced sensitivity
          const horizontalAngle = Math.atan2(targetX, targetZ) * 0.7;
          const distance = Math.sqrt(targetX * targetX + targetZ * targetZ);
          const verticalAngle = Math.atan2(targetY, distance) * 0.5;
          
          const currentYaw = object.rotation.y;
          const currentPitch = object.rotation.x;
          
          // Reduced maximum rotation for subtler movement
          const maxRotation = THREE.MathUtils.degToRad(30);
          const smoothing = 0.25;
          
          // Calculate new rotations with smoothing
          const targetYaw = horizontalAngle;
          const targetPitch = -verticalAngle;
          
          // Apply different rotation amounts based on which bone we're updating
          const rotationMultiplier = object.name === "Head" ? 1 : 0.25; // Head.001 moves less
          
          const newYaw = THREE.MathUtils.lerp(
            currentYaw,
            targetYaw * rotationMultiplier,
            smoothing
          );
          
          const newPitch = THREE.MathUtils.lerp(
            currentPitch,
            targetPitch * rotationMultiplier,
            smoothing
          );
          
          // Apply rotation with clamping
          object.rotation.y = THREE.MathUtils.clamp(
            newYaw,
            -maxRotation * 1.1 * rotationMultiplier,
            maxRotation * 1.1 * rotationMultiplier
          );
          
          object.rotation.x = THREE.MathUtils.clamp(
            newPitch,
            -maxRotation * 0.75 * rotationMultiplier,
            maxRotation * 0.75 * rotationMultiplier
          );
        }
      });
    }
  });
  useFrame(({ clock }) => {
    if (modelRef.current) {
      modelRef.current.traverse((object) => {
        if (object.isBone) {
          // Base speed that stays constant
          const baseWagSpeed = 2.5; // Slower base speed
          
          // Tail wagging animation
          if (object.name === 'TailStart') {
            // Random variation in amplitude, not speed
            const wagAmount = 0.3 + (Math.sin(clock.getElapsedTime() * 0.2) * 0.05); // Subtle random intensity
            const leftRightAmount = 0.2 + (Math.sin(clock.getElapsedTime() * 0.15) * 0.03); // Subtle left-right variation
            
            // Add small noise to the motion while keeping consistent speed
            const noise = Math.sin(clock.getElapsedTime() * 0.8) * 0.02;
            const wagRotation = (Math.sin(clock.getElapsedTime() * baseWagSpeed) * wagAmount) + noise;
            const leftRightRotation = (Math.cos(clock.getElapsedTime() * baseWagSpeed) * leftRightAmount) + noise;
            
            // Combine vertical and horizontal motion
            object.rotation.x = -1 + leftRightRotation;
            object.rotation.y = wagRotation;
            object.rotation.z = wagRotation * 0.75;
          }
          
          // Make the tail end follow with random variations, but speed limited
          if (object.name === 'TailEnd') {
            const phaseOffset = 0.1; // Consistent delay
            
            // Update random direction every 1-2 seconds
            if (!object.userData.nextDirectionChange) {
              object.userData.nextDirectionChange = clock.getElapsedTime() + 1 + Math.random();
              object.userData.randomDirection = Math.random() > 0.5 ? 1 : -1;
            }
            
            if (clock.getElapsedTime() > object.userData.nextDirectionChange) {
              object.userData.nextDirectionChange = clock.getElapsedTime() + 1 + Math.random();
              object.userData.randomDirection = Math.random() > 0.5 ? 1 : -1;
            }
            
            const wagAmount = 0.4;
            const leftRightAmount = 0.3;
            const noise = Math.sin(clock.getElapsedTime() * 0.8) * 0.02;
            
            const wagRotation = (Math.sin((clock.getElapsedTime() - phaseOffset) * baseWagSpeed) * wagAmount * object.userData.randomDirection) + noise;
            const leftRightRotation = (Math.cos((clock.getElapsedTime() - phaseOffset) * baseWagSpeed) * leftRightAmount) + noise;
            
            // Smoothly interpolate to target rotations
            if (!object.userData.currentRotation) {
              object.userData.currentRotation = {
                x: -0.24860985577106476,
                y: 0,
                z: 0
              };
            }
            
            const smoothing = 0.05;
            object.userData.currentRotation.x = THREE.MathUtils.lerp(object.userData.currentRotation.x, -0.24860985577106476 + leftRightRotation, smoothing);
            object.userData.currentRotation.y = THREE.MathUtils.lerp(object.userData.currentRotation.y, wagRotation, smoothing);
            object.userData.currentRotation.z = THREE.MathUtils.lerp(object.userData.currentRotation.z, wagRotation, smoothing);
            
            object.rotation.x = object.userData.currentRotation.x;
            object.rotation.y = object.userData.currentRotation.y;
            object.rotation.z = object.userData.currentRotation.z;
          }
        }
      });
    }
  });

  useFrame(({ clock }) => {
    if (modelRef.current) {
      modelRef.current.traverse((object) => {
        if (object.isBone) {
          // Add subtle spine sway
          if (object.name === 'LowerSpine' || object.name === 'UpperSpine') {
            const swaySpeed = 1.2; // Slow movement
            const swayAmount = object.name === 'LowerSpine' ? 0.03 : 0.02; // Less movement for upper spine
            
            // Create a gentle figure-8 pattern
            const time = clock.getElapsedTime();
            const swayX = Math.sin(time * swaySpeed) * swayAmount;
            const swayZ = Math.cos(time * swaySpeed * 0.5) * swayAmount * 0.5;
            
            // Smoothly interpolate to new rotation
            object.rotation.x = THREE.MathUtils.lerp(
              object.rotation.x,
              object.name === 'LowerSpine' ? -0.01438760757446289 + swayX : swayX,
              0.05
            );
            object.rotation.z = THREE.MathUtils.lerp(
              object.rotation.z,
              swayZ,
              0.05
            );
          }
        }
      });
    }
  });

  return (
    <primitive 
      ref={modelRef} 
      object={gltf.scene} 
      scale={1.5}
      position={[0, -1.1, 5]}
    />
  );
});

export default Model; 